// src/components/SmartSearch/SmartSearchBar.js
import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Typography,
  Chip,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Search,
  Clear,
  TrendingUp,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { propertiesAPI } from '../../services/api';

const SmartSearchBar = ({ 
  variant = 'large', // 'large' para Home, 'small' para Navbar
  onSearch,
  placeholder = "Ex: Apartamento 3 quartos em Copacabana até R$ 800 mil",
  autoFocus = false,
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);

  // Exemplos de buscas populares
  const popularSearches = [
    "Apartamento 2 quartos centro",
    "Casa com piscina",
    "Terreno comercial",
    "Apartamento mobiliado aluguel",
  ];

  // Debounce para sugestões
  useEffect(() => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(() => {
      fetchSuggestions();
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Fechar sugestões ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = async () => {
    try {
      setLoading(true);
      const response = await propertiesAPI.searchSuggestions(query);
      setSuggestions(response.data.suggestions || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Erro ao buscar sugestões:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (searchQuery = query) => {
    if (!searchQuery.trim()) return;

    setShowSuggestions(false);
    
    if (onSearch) {
      onSearch(searchQuery);
    } else {
      navigate(`/busca?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    handleSearch(suggestion);
  };

  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const isLarge = variant === 'large';

  return (
    <Box ref={searchRef} sx={{ position: 'relative', width: '100%' }}>
      <TextField
        fullWidth
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyPress={handleKeyPress}
        onFocus={() => setShowSuggestions(query.length >= 3 || true)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        size={isLarge ? 'medium' : 'small'}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {loading && <CircularProgress size={20} />}
              {query && !loading && (
                <IconButton size="small" onClick={handleClear}>
                  <Clear />
                </IconButton>
              )}
            </InputAdornment>
          ),
          sx: {
            bgcolor: 'background.paper',
            borderRadius: isLarge ? 2 : 1,
            fontSize: isLarge ? '1.1rem' : '0.95rem',
          }
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: 'primary.main',
            },
          },
        }}
      />

      {/* Dropdown de Sugestões */}
      {showSuggestions && (
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            mt: 1,
            maxHeight: 400,
            overflow: 'auto',
            zIndex: 1300,
          }}
        >
          {/* Sugestões baseadas na query */}
          {suggestions.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                sx={{ px: 2, pt: 2, pb: 1, display: 'block', color: 'text.secondary' }}
              >
                Sugestões
              </Typography>
              <List dense>
                {suggestions.map((suggestion, index) => (
                  <ListItem
                    key={index}
                    button
                    onClick={() => handleSuggestionClick(suggestion)}
                    sx={{
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <Search fontSize="small" sx={{ mr: 2, color: 'action.active' }} />
                    <ListItemText primary={suggestion} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Buscas Populares */}
          {(!query || query.length < 3) && (
            <Box>
              <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center' }}>
                <TrendingUp fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="caption" color="text.secondary">
                  Buscas Populares
                </Typography>
              </Box>
              <List dense>
                {popularSearches.map((search, index) => (
                  <ListItem
                    key={index}
                    button
                    onClick={() => handleSuggestionClick(search)}
                    sx={{
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <TrendingUp fontSize="small" sx={{ mr: 2, color: 'action.active' }} />
                    <ListItemText primary={search} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Exemplos de uso */}
          {query.length === 0 && isLarge && (
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                Exemplos de busca:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                <Chip
                  label="Apartamento 3 quartos"
                  size="small"
                  onClick={() => handleSuggestionClick("Apartamento 3 quartos")}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="Casa até R$ 500 mil"
                  size="small"
                  onClick={() => handleSuggestionClick("Casa até R$ 500 mil")}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="Terreno comercial centro"
                  size="small"
                  onClick={() => handleSuggestionClick("Terreno comercial centro")}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default SmartSearchBar;